import CryptoJS from 'crypto-js'
import { Project } from 'common/project'

const KEY = '686f78746f6e6d69782d696d6167652d70726f7879'
const SALT = '32587a4879754a347a4c406662506f714624334a'
const urlSafeBase64 = (string: string) => {
  return btoa(string).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
}

const hexDecode = (hex: string) => {
  const res = CryptoJS.enc.Hex.parse(hex)
  return res
}

const sign = (salt: string, target: string, secret: string) => {
  const hmac = CryptoJS.algo.HMAC.create(
    CryptoJS.algo.SHA256,
    hexDecode(secret),
  )
  hmac.update(hexDecode(salt))
  hmac.update(target)
  return CryptoJS.enc.Base64.stringify(hmac.finalize())
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
}

const normalizeUrl = (inputUrl: string): string => {
  let url = inputUrl

  // Ensure URL starts with /
  if (
    !url.startsWith('/') &&
    !url.startsWith('http') &&
    !url.startsWith('www')
  ) {
    url = `/${url}`
  }

  return url
}

const stripStorybookUrl = (url: string, debug: boolean): string => {
  if (!debug && url.startsWith('/static/media')) {
    return url.replace('/static/media/public/', '/')
  }
  return url
}

const shouldUseImageProxy = (url: string, debug: boolean): boolean => {
  return !(
    !Project.imageProxy ||
    url.includes(Project.imageProxy) ||
    __DEV__ ||
    debug
  )
}

const getServerSideUrl = (url: string): string => {
  let siteUrl = process.env.NEXT_PUBLIC_SITE_URL

  if (!siteUrl) {
    siteUrl = constructVercelUrl() || 'https://www.hoxtonmix.com'
  }

  return `${siteUrl}${url}`
}

const constructVercelUrl = (): string | null => {
  const branch = process.env.NEXT_PUBLIC_BRANCH_NAME
  const projectName = process.env.NEXT_PUBLIC_PROJECT_NAME

  if (branch && projectName && process.env.VERCEL_ENV === 'preview') {
    const alias = `${projectName}-${branch}`
      .replace(/_/g, '-')
      .replace(/\//g, '-')
      .replace(/\./g, '-')
    const constructedUrl = `https://${alias}.vercel.app`
    return constructedUrl
  }

  if (process.env.NEXT_PUBLIC_VERCEL_URL) {
    return `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`
  }

  return null
}

const buildFullUrl = (url: string): string => {
  const isClientSide = typeof window !== 'undefined'
  const isRelativeUrl = !url.startsWith('http') && !url.startsWith('www')

  if (isClientSide && isRelativeUrl) {
    return document.location.origin + url
  }

  if (!isClientSide && isRelativeUrl) {
    return getServerSideUrl(url)
  }

  return url
}

export default function imageResize(
  urlInput: string | { default: { src: string } },
  width: number | null = null,
  height: number | null = null,
  format = 'webp',
  debug = false,
) {
  // Extract URL from input
  const extractedUrl =
    typeof urlInput === 'string' ? urlInput : urlInput?.default?.src
  if (!extractedUrl) return extractedUrl

  // Normalize and clean URL
  let url = normalizeUrl(extractedUrl)
  url = stripStorybookUrl(url, debug)

  // Early return if image proxy shouldn't be used
  if (!shouldUseImageProxy(url, debug)) {
    return url
  }

  // Determine final format (SVGs stay as SVG)
  const finalFormat = url.endsWith('.svg') ? 'svg' : format

  // Build full URL
  url = buildFullUrl(url)

  // Add cache-busting parameter from build
  const cacheVersion = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA

  if (cacheVersion) {
    const separator = url.includes('?') ? '&' : '?'
    url = `${url}${separator}v=${cacheVersion}`
  }

  // Generate image proxy URL
  const encodedUrl = urlSafeBase64(url)
  const devicePixelRatio =
    typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1

  const processingOptions = width
    ? `/size:${parseInt(`${width}`)}:${parseInt(
        `${height}`,
      )}/resizing_type:fill/dpr:${devicePixelRatio}/format:${finalFormat}/`
    : `/resizing_type:fill/dpr:${devicePixelRatio}/format:${finalFormat}/`

  const path = `${processingOptions}${encodedUrl}.${finalFormat}`
  const signature = sign(SALT, path, KEY)

  return `${Project.imageProxy}${signature}${path}`
}

export function generateSrcSet(
  _url: string | { default: { src: string } },
  width: number,
  height: number,
  format = 'webp',
  dpiSteps: number[] = [1, 2],
) {
  if (typeof _url === 'string' && _url.includes('.svg')) {
    return {
      src: _url,
      srcSet: '',
    }
  }

  return {
    src: imageResize(_url, width, height, format, false),
    srcSet: dpiSteps
      .map((dpi) => {
        const url = imageResize(_url, width, height, format, false)
        return `${url} ${dpi}x`
      })
      .join(', '),
  }
}
